// The recent-engagement session read — the NARROW door-reachable arm of the
// v2.2 session list, for a consumer that only needs "what has this user
// recorded lately" and has no list UI, no filters and no toolbar.
//
// WHY THIS IS NOT `useQuerySessionsList` BEHIND THE DOOR. That hook's params
// carry `selectedDateRange: DateRange` from the design system's date-range
// picker, and `/data` is headless — the package's own
// `manifest-boundary.test.ts` refuses that import. The narrow shape is also the
// correct one: an engagement counter wants the unfiltered `scope: me` seed, and
// every filter it could pass would fork the cache.
//
// AND IT MINTS NO IDENTITY OF ITS OWN. The key below is
// `JSON.stringify`-equal to what the app's 11-element
// `buildSessionsListQueryKey` resolves to for the unfiltered `scope: me`
// defaults, so this hook subscribes to the entry the real session list already
// populates: no extra request, no extra PHI at rest, and none of the desktop
// offline cache's twenty filter buckets consumed. Drift here is silent in
// production — hence `useQueryRecentSessionEngagement.test.ts`.

import { useInfiniteQuery } from '@tanstack/react-query';

import {
  SessionScope,
  SessionSortBy,
  Shared__enums__enums__SortOrder as SortOrder,
} from '@heidi/schema/ml-scribe';

import {
  GET_SESSIONS_LIST,
  SESSIONS_PAGE_SIZE,
  SESSION_LIST_DEFAULT_SORT_BY,
  SESSION_LIST_DEFAULT_SORT_ORDER,
  type SessionListItem,
  type SessionListResponse,
} from '../../contracts';
import { getSessionsListV2, type SessionsListParams } from '../api/list';
import { getSessionDataConfig } from '../config';
import { SESSIONS_REFETCH_INTERVAL } from './sessionsList';

/**
 * The unfiltered `scope: me` list identity, element for element as the app's
 * `buildSessionsListQueryKey` builds it: root, scope, statuses, date from, date
 * to, patient profile ids, clinician ids, sort by, sort order, search query,
 * urgent-only. The two `undefined` id slots are what the app's
 * `getEffectivePatientProfileIds` / `getEffectiveClinicianIds` return for an
 * empty selection — NOT `[]`, which would be a different bucket.
 */
export const RECENT_SESSION_ENGAGEMENT_QUERY_KEY = [
  GET_SESSIONS_LIST,
  SessionScope.me,
  [],
  undefined,
  undefined,
  undefined,
  undefined,
  SESSION_LIST_DEFAULT_SORT_BY,
  SESSION_LIST_DEFAULT_SORT_ORDER,
  '',
  false,
] as const;

/** The request the identity above stands for — the app's
 *  `buildSessionsListRequest` for the same params. Every filter field is
 *  omitted rather than sent empty, which is what that builder resolves to. */
const buildRecentSessionEngagementRequest = (
  cursor: string | undefined
): SessionsListParams => ({
  scope: SessionScope.me,
  page_size: SESSIONS_PAGE_SIZE,
  cursor,
  sort_by: SessionSortBy.created_at,
  sort_order: SortOrder.DESC,
});

/** Flatten the pages this hook returns into one row list, newest first. */
export function flattenSessionEngagementPages(
  pages: SessionListResponse[] | undefined
): SessionListItem[] {
  // `?? []` inside the flatMap as well as outside it: a page that arrives
  // without a `sessions` array would otherwise be appended as an `undefined`
  // row rather than flattened away (SCR-7433).
  return pages?.flatMap((page) => page.sessions ?? []) ?? [];
}

/**
 * The caller's own gate — "is there anything for this read to unlock yet" —
 * ANDed with the three readiness conditions the real list gates on: a token, a
 * loaded user and (on desktop) a reachable backend.
 *
 * The user read is a PRESENCE check, not an identity: the request identifies
 * the caller from the bearer token, so this only needs to know a user is
 * loaded. It crosses the seam already resolved, as `isUserLoaded`.
 *
 * Deliberately a passive reader of the shared cache entry: no first-page poll
 * and no desktop-cache write, both of which belong to the list that owns the
 * screen.
 */
export function useQueryRecentSessionEngagement(enabled = true) {
  const config = getSessionDataConfig();
  const token = config.useAuthToken?.();
  const { isDesktopOnlineReady, isUserLoaded } = config.useSessionListEnvironment();

  return useInfiniteQuery({
    queryKey: RECENT_SESSION_ENGAGEMENT_QUERY_KEY,
    enabled: Boolean(token && isUserLoaded && isDesktopOnlineReady) && enabled,
    // Matches the real list's staleness so a mount inside the window serves the
    // cached pages instead of refetching what the list just fetched.
    staleTime: SESSIONS_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async ({ pageParam }): Promise<SessionListResponse> => {
      const response = await getSessionsListV2(
        buildRecentSessionEngagementRequest(pageParam)
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}
